/**
 * RolloutGroups — GRPO's unit of learning, made visible: each group is
 * one prompt's rollouts, and every rollout's advantage is its reward
 * measured against the group mean. The bar shows the reward with the
 * group baseline as a tick, so what the optimizer pushes toward (right
 * of the tick) and away from (left of it) reads at a glance.
 */

import { useState } from 'react'

import type { TrainingRolloutGroup } from '@/api/generated/model'
import { Badge } from '@/components/ui/badge'

const GROUPS_PER_PAGE = 10

interface PageSummary {
  groups: TrainingRolloutGroup[]
  stepStart: number
  stepEnd: number
  meanReward: number
}

function summarizePages(groups: TrainingRolloutGroup[]): PageSummary[] {
  const pages: PageSummary[] = []
  for (let start = 0; start < groups.length; start += GROUPS_PER_PAGE) {
    const slice = groups.slice(start, start + GROUPS_PER_PAGE)
    const steps = slice.map((g) => g.step)
    const rewards = slice.flatMap((g) => g.rollouts.map((r) => r.reward))
    pages.push({
      groups: slice,
      stepStart: Math.min(...steps),
      stepEnd: Math.max(...steps),
      meanReward: rewards.reduce((n, r) => n + r, 0) / rewards.length,
    })
  }
  return pages
}

function groupMean(group: TrainingRolloutGroup): number {
  return group.rollouts.reduce((n, r) => n + r.reward, 0) / group.rollouts.length
}

function advantageTone(advantage: number): string {
  if (advantage > 0.005) return 'text-success'
  if (advantage < -0.005) return 'text-destructive'
  return 'text-muted-foreground'
}

function formatAdvantage(advantage: number): string {
  const sign = advantage > 0 ? '+' : ''
  return `${sign}${advantage.toFixed(2)}`
}

/** Reward bar with the group-mean baseline tick the advantage hangs off. */
function RewardBar({ reward, mean }: { reward: number; mean: number }): React.JSX.Element {
  return (
    <div className="relative h-1.5 w-full rounded-[1.5px] bg-border/60">
      <div
        className="h-full rounded-[1.5px] bg-brand-gunmetal-blue"
        style={{ width: `${(reward * 100).toString()}%` }}
      />
      <div
        className="absolute -top-[3px] h-3 w-px bg-foreground/70"
        style={{ left: `${(mean * 100).toString()}%` }}
      />
    </div>
  )
}

function GroupCard({ group }: { group: TrainingRolloutGroup }): React.JSX.Element {
  const mean = groupMean(group)
  return (
    <div className="border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-mono text-xs">{group.sample}</span>
          <span className="text-sm text-foreground">{group.task_name}</span>
          <span className="font-mono text-[10px] text-muted-foreground">
            {group.task_id}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">step {group.step.toLocaleString()}</Badge>
          <Badge variant="outline">
            {group.rollouts.length} rollouts · mean {mean.toFixed(2)}
          </Badge>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {group.rollouts.map((rollout, index) => {
          const advantage = rollout.reward - mean
          return (
            <div
              key={index}
              className="grid grid-cols-[2rem_1fr_2.6rem_3.2rem] items-center gap-x-3 sm:grid-cols-[2rem_9rem_2.6rem_3.2rem_1fr]"
            >
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                R{index + 1}
              </span>
              <RewardBar reward={rollout.reward} mean={mean} />
              <span className="text-right font-mono text-xs tabular-nums">
                {rollout.reward.toFixed(2)}
              </span>
              <span
                className={`text-right font-mono text-xs tabular-nums ${advantageTone(advantage)}`}
              >
                {formatAdvantage(advantage)}
              </span>
              <span className="col-span-4 pb-1 text-xs text-muted-foreground sm:col-span-1 sm:truncate sm:pb-0">
                {rollout.note}
              </span>
            </div>
          )
        })}
      </div>
      <p className="mt-3 border-t border-border pt-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        Advantage = reward − group mean ({mean.toFixed(2)})
      </p>
    </div>
  )
}

/** Page selector where each page shows its step range and mean reward. */
function PagePicker({
  pages,
  pageIndex,
  onSelect,
}: {
  pages: PageSummary[]
  pageIndex: number
  onSelect: (index: number) => void
}): React.JSX.Element {
  return (
    <nav aria-label="Rollout group pages" className="flex flex-wrap items-center gap-1.5">
      {pages.map((page, index) => {
        const selected = index === pageIndex
        return (
          <button
            key={index}
            type="button"
            onClick={() => {
              onSelect(index)
            }}
            aria-pressed={selected}
            className={`border border-border px-2.5 py-1.5 text-left font-mono text-[10px] uppercase tracking-wider transition-colors ${
              selected ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/50'
            }`}
          >
            <span className="block tabular-nums">
              steps {page.stepStart.toLocaleString()}&ndash;{page.stepEnd.toLocaleString()}
            </span>
            <span className="block tabular-nums">mean {page.meanReward.toFixed(2)}</span>
          </button>
        )
      })}
    </nav>
  )
}

export function RolloutGroups({ groups }: { groups: TrainingRolloutGroup[] }): React.JSX.Element {
  const [pageIndex, setPageIndex] = useState(0)
  const pages = summarizePages(groups)
  const current = pages[Math.min(pageIndex, pages.length - 1)]
  if (current === undefined) {
    throw new Error('RolloutGroups rendered with no groups; the caller must guard on length')
  }

  return (
    <div className="space-y-3">
      {pages.length > 1 && (
        <PagePicker pages={pages} pageIndex={pageIndex} onSelect={setPageIndex} />
      )}
      {current.groups.map((group) => (
        <GroupCard key={group.sample} group={group} />
      ))}
    </div>
  )
}
